import type {
  SuccessRateByVersionResponse,
  SuccessRateByVersionRow,
} from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface SuccessRateByVersionProps {
  data: SuccessRateByVersionResponse;
}

/** Newest-looking version first; `unknown` always last, never mixed into the ranking. */
function sortedVersionRows(
  versions: Record<string, SuccessRateByVersionRow>
): Array<[string, SuccessRateByVersionRow]> {
  return Object.entries(versions).sort(([a], [b]) => {
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return b.localeCompare(a, undefined, { numeric: true, sensitivity: "base" });
  });
}

/**
 * Sygnał #12 druga połowa (checkpoint `2026-07-25-v2m-projekt-metryk-12-sygnalow.md`,
 * V2M-DEV-012): skuteczność generacji rozbita na wersję wtyczki. Endpoint
 * `GET /admin/metrics/success-rate-by-version` istniał od commitu `94ea921c`
 * bez ani jednego konsumenta w panelu — ten komponent jest tym konsumentem.
 *
 * Dlaczego per wersja, a nie jedna liczba: regresja wprowadzona nową wersją
 * wtyczki rozpuszcza się w globalnej średniej dokładnie wtedy, gdy jest
 * najgroźniejsza — tuż po wypuszczeniu, gdy nowa wersja jest mniejszością
 * ruchu. Rozbicie po wersji jest jedynym kształtem, w którym to widać.
 *
 * `hoursSinceLastEventEver === null` (ten sam rozdział, co w `RecordingCapHits`
 * i `UnitEconomics`): pusty wykaz wersji może znaczyć „spokojna doba" ALBO
 * „kolekcja `generations` nigdy nie dostała zapisu". To drugie jest awarią
 * pomiaru i musi być głośne, nie neutralne.
 */
export function SuccessRateByVersion({ data }: SuccessRateByVersionProps) {
  if (data.hoursSinceLastEventEver === null) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">
          NIGDY nie zapisano do <code>generations</code> — ani razu, w całej historii, nie tylko w
          ostatnie {data.days} {data.days === 1 ? "dobę" : "dni"}. To nie jest zero generacji, to
          brak pomiaru.
        </div>
      </div>
    );
  }

  const rows = sortedVersionRows(data.versions);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Ostatnie {data.days} {data.days === 1 ? "doba" : "dni"} — udział generacji zakończonych
        sukcesem w każdej wersji wtyczki osobno. Kubełek <code>unknown</code> to żądania bez
        nagłówka <code>X-Plugin-Version</code>.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MetricCard
          label="Godzin od ostatniej generacji (kiedykolwiek)"
          value={data.hoursSinceLastEventEver.toFixed(1)}
        />
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-600">
          Zmierzone zero — w tym oknie nie było żadnej generacji, ale warstwa pomiarowa żyje
          (ostatni zapis {data.hoursSinceLastEventEver.toFixed(1)} h temu).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="sticky left-0 z-10 bg-slate-50 py-2 pr-4 font-medium">Wersja</th>
                <th className="py-2 pr-4 font-medium">Wszystkich</th>
                <th className="py-2 pr-4 font-medium">Udanych</th>
                <th className="py-2 pr-4 font-medium">Nieudanych</th>
                <th className="py-2 pr-4 font-medium">Skuteczność</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([version, row]) => (
                <tr key={version} className="border-t border-slate-200">
                  <td className="sticky left-0 z-10 bg-slate-50 py-2 pr-4 font-mono">{version}</td>
                  <td className="py-2 pr-4">{row.total}</td>
                  <td className="py-2 pr-4">{row.completed}</td>
                  <td className="py-2 pr-4">{row.failed}</td>
                  <td className="py-2 pr-4 font-semibold">{(row.successRate * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
